/**
 * Shared refetch specs for GraphQL mutations that need to refresh the
 * live `useContractExtraction` observer after they complete.
 *
 * Apollo keys observer subscriptions by document + variables, so a
 * `refetchQueries` entry that omits `archived: false` lands on a
 * different cache cell than the drawer's observer and the UI never
 * updates. SE-4603 was one failure mode of exactly that mismatch.
 * Every mutation hook that writes to a contract extraction must route
 * its refetch through this module so the variable shape can never
 * drift from the observer's.
 * @module features/contracts/hooks/contractExtractionRefetch
 */

import type { PureQueryOptions } from "@apollo/client";

import { GET_CONTRACT_EXTRACTION_QUERY } from "./useContractExtraction";

/**
 * Returns the `refetchQueries` entry for the live, non-archived contract
 * extraction observer. Triggering, saving, deleting, and editing an
 * extraction are all only permitted on active rows (the backend role
 * gates enforce this), so `archived` is always `false` here.
 * @param playerId - Player UUID the mutation was run against
 * @param documentType - Document type the mutation was run against
 * @returns Apollo `PureQueryOptions` entry for `GetContractExtraction` keyed to the active row
 */
export function activeContractExtractionRefetch(
  playerId: string,
  documentType: string
): PureQueryOptions {
  return {
    query: GET_CONTRACT_EXTRACTION_QUERY,
    variables: { playerId, documentType, archived: false },
  };
}
